
import { supabase } from '../integrations/supabase/client';

export interface UserTokens {
  id: string;
  user_id: string;
  token_balance: number;
  created_at: string;
  updated_at: string;
}

export interface TokenUsageLog {
  id: string;
  user_id: string;
  session_id?: string;
  tokens_used: number;
  input_tokens: number;
  output_tokens: number;
  model_used: string;
  created_at: string;
}

export interface TokenPurchase {
  id: string;
  user_id: string;
  stripe_session_id: string;
  stripe_payment_intent_id?: string;
  token_amount: number;
  amount_paid: number;
  currency: string;
  status: string;
  created_at: string;
  completed_at?: string;
}

export const tokenService = {
  // Get user's current token balance
  async getUserTokenBalance(): Promise<number> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('user_tokens')
      .select('token_balance')
      .eq('user_id', user.id)
      .single();

    if (error) {
      console.error('Error fetching token balance:', error);
      throw error;
    }

    return data?.token_balance || 0;
  },

  // Check if user has enough tokens for a request
  async hasEnoughTokens(requiredTokens: number): Promise<boolean> {
    const balance = await this.getUserTokenBalance();
    return balance >= requiredTokens;
  },

  // Get user's token usage logs
  async getTokenUsageLogs(limit: number = 50): Promise<TokenUsageLog[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('token_usage_logs')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching usage logs:', error);
      throw error;
    }

    return data || [];
  },

  // Get user's purchase history
  async getPurchaseHistory(): Promise<TokenPurchase[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('token_purchases')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching purchase history:', error);
      throw error;
    }

    return data || [];
  },

  // Subscribe to token balance changes with both user_tokens and token_usage_logs
  subscribeToTokenBalance(callback: (balance: number) => void) {
    const userTokensChannel = supabase
      .channel('user-tokens-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_tokens'
        },
        (payload) => {
          console.log('💰 Token balance updated via user_tokens:', payload.new);
          callback((payload.new as UserTokens).token_balance);
        }
      )
      .subscribe();

    // Also listen to usage logs to refresh balance immediately after token usage
    const usageLogsChannel = supabase
      .channel('token-usage-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'token_usage_logs'
        },
        async (payload) => {
          console.log('💰 Token usage logged:', payload.new);
          // Refresh the balance after usage is logged
          try {
            const newBalance = await this.getUserTokenBalance();
            callback(newBalance);
          } catch (error) {
            console.error('Error refreshing balance after usage:', error);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(userTokensChannel);
      supabase.removeChannel(usageLogsChannel);
    };
  }
};
